// iter181 — Post-submission confirmation screen for vendor/business
// applications. Full-viewport dark-luxury layout with a large gold
// check icon, Playfair thank-you title, kind-specific description, and
// a single gold-gradient "Back to Home" CTA that navigates to `/`
// (which auto-renders Ride or Discover per persisted `habano.mode`).
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { getMode } from "../lib/appMode";

export default function ApplicationSubmitted() {
  const { kind = "business" } = useParams();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  // iter202 — Auto-approved submissions land here with ?approved=auto
  // and get the "You're live!" copy. Otherwise show the review copy.
  const isAuto = params.get("approved") === "auto";

  const title = isAuto
    ? (lang === "es" ? "¡Ya estás en vivo en Habano!" : "You are now live on Habano!")
    : (lang === "es" ? "¡Gracias!" : t("apply_thanks_title"));
  const sub = isAuto
    ? (lang === "es"
        ? "Empieza a recibir clientes hoy."
        : "Start receiving customers today.")
    : (lang === "es"
        ? "Nuestro equipo revisará tu solicitud y se pondrá en contacto contigo pronto."
        : "Our team will review your application and get back to you soon.");

  const goHome = () => {
    // Read the persisted mode purely for observability/state passing —
    // "/" is the correct home route for both modes (IndexRouter picks).
    const mode = getMode();
    nav("/", { state: { fromApplicationThanks: true, mode } });
  };

  return (
    <main
      data-testid="apply-thanks-page"
      className="min-h-screen grid place-items-center px-6"
      style={{ background: "#0B0B0B" }}
    >
      <div className="w-full max-w-sm text-center flex flex-col items-center">
        <CheckCircle2
          data-testid="apply-thanks-icon"
          strokeWidth={1.4}
          style={{ width: 64, height: 64, color: "#E8B84B" }}
        />

        <h1
          data-testid={isAuto ? "apply-thanks-title-auto" : "apply-thanks-title"}
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 26,
            color: "#F5F1E8",
            marginTop: 20,
            lineHeight: 1.15,
          }}
        >
          {title}
        </h1>

        <p
          data-testid="apply-thanks-sub"
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 15,
            color: "#B0AAA3",
            marginTop: 14,
            lineHeight: 1.6,
          }}
        >
          {sub}
        </p>

        <button
          type="button"
          data-testid="apply-thanks-home"
          onClick={goHome}
          className="w-full inline-flex items-center justify-center transition-opacity hover:opacity-95"
          style={{
            marginTop: 32,
            height: 52,
            background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
            color: "#0B0B0B",
            borderRadius: 12,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: "0.04em",
            border: "none",
            boxShadow: "0 4px 16px rgba(232,184,75,0.35)",
          }}
        >
          {t("apply_thanks_home_btn")}
        </button>
      </div>
    </main>
  );
}
